<script setup lang="ts">
defineOptions({ name: "SiteTemplateModal" });

const emit = defineEmits<{ (e: "saved"): void }>();

const { t } = useI18n();
const toast = useToast();

const isOpen = ref(false);

const categoryOptions = [
  { value: "conference", label: t("siteTemplate.categoryConference") },
  { value: "workshop", label: t("siteTemplate.categoryWorkshop") },
  { value: "festival", label: t("siteTemplate.categoryFestival") },
  { value: "exhibition", label: t("siteTemplate.categoryExhibition") },
  { value: "wedding", label: t("siteTemplate.categoryWedding") },
  { value: "trade-show", label: t("siteTemplate.categoryTradeShow") },
  { value: "concert", label: t("siteTemplate.categoryConcert") },
  { value: "sports-event", label: t("siteTemplate.categorySportsEvent") },
  { value: "fundraiser", label: t("siteTemplate.categoryFundraiser") },
  { value: "networking", label: t("siteTemplate.categoryNetworking") },
  { value: "meetup", label: t("siteTemplate.categoryMeetup") },
  { value: "other", label: t("siteTemplate.categoryOther") },
];

const kindOptions = [
  { value: "event-landing", label: t("siteTemplate.kindEventLanding") },
  { value: "store", label: t("siteTemplate.kindStore") },
];

const {
  formModel,
  formErrors,
  errorMsg,
  isCreate,
  saveLoading,
  loadLoading,
  save,
  load,
  reset,
} = useCrud<any>({
  endpoint: "/site-templates",
  defaultFormModel: () => ({
    id: "",
    name: "",
    description: "",
    category: "other",
    featuredAssetID: "",
    kind: "event-landing",
    accessLevel: "public",
    isActive: true,
    isDefault: false,
  }),
});

async function open(item?: any) {
  reset();
  if (item) {
    try {
      await load(item.id);
    } catch {
      if (errorMsg.value) toast.error({ title: errorMsg.value });
      return;
    }
  }
  isOpen.value = true;
}

async function submit() {
  try {
    await save();
    isOpen.value = false;
    toast.success({ title: t("message.saveSuccess") });
    emit("saved");
  } catch {
  }
}

defineExpose({ open });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="isCreate ? t('siteTemplate.createTitle') : t('siteTemplate.editTitle')"
    size="md"
    @close="isOpen = false"
  >
    <div class="flex flex-col gap-medium">
      <FormField
        v-model="formModel.name"
        :label="t('field.name')"
        :placeholder="t('field.namePlaceholder')"
        :error="formErrors.name"
      />

      <FormTextarea
        v-model="formModel.description"
        :label="t('field.description')"
        :placeholder="t('field.descriptionPlaceholder')"
        :error="formErrors.description"
      />

      <AssetUpload
        v-model="formModel.featuredAssetID"
        :label="t('siteTemplate.featuredImage')"
        :error="formErrors.featuredAssetID"
        file-list="single"
      />

      <FormSelect
        v-model="formModel.category"
        :label="t('field.category')"
        :options="categoryOptions"
        :placeholder="t('siteTemplate.categoryPlaceholder')"
        :error="formErrors.category"
      />

      <FormSelect
        v-model="formModel.kind"
        :label="t('siteTemplate.kind')"
        :options="kindOptions"
        :error="formErrors.kind"
      />

      <FormSelect
        v-model="formModel.accessLevel"
        :label="t('siteTemplate.accessLevel')"
        :options="[
          { value: 'public', label: t('siteTemplate.accessPublic') },
          { value: 'private', label: t('siteTemplate.accessPrivate') },
          { value: 'protected', label: t('siteTemplate.accessProtected') },
        ]"
        :error="formErrors.accessLevel"
      />

      <FormSwitch v-model="formModel.isActive" :label="t('siteTemplate.isActive')" />
      <FormSwitch v-model="formModel.isDefault" :label="t('siteTemplate.isDefault')" />

      <InlineMessage v-if="errorMsg" variant="negative">{{ errorMsg }}</InlineMessage>
    </div>

    <template #actions>
      <Button variant="tertiary" size="medium" @click="isOpen = false">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="medium"
        :disabled="saveLoading || loadLoading"
        @click="submit"
      >
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
